// CE-04g-UI-pivots-popover — pure helper. Selects the prior trading day's
// daily OHLC bar from a list of daily bars, given today's ET date key and a
// NYSE trading calendar. Returns { high, low, close } or null when no
// matching bar exists, so the chart can fall back to its existing
// intraday-aggregate pivot path.
//
// CRITICAL date-keying rule: IBKR daily bars carry their date as a
// YYYYMMDD string or number. `toCandles` converts those to a UTC-midnight
// `rawEpochMs`, which `getEtSessionDateKey` interprets as the PRIOR ET
// calendar date (because UTC midnight is the same as the prior day's
// 19:00–20:00 in ET). Date-anchored bars must therefore be keyed directly
// from `bar.time` as `YYYY-MM-DD`, NOT through `getEtSessionDateKey`. The
// intraday path (real epoch instants) still uses `getEtSessionDateKey`.

import {
  getPreviousEtTradingDayKey,
  getEtSessionDateKey,
} from './sessionBoundary.js'

function dateKeyFromIbkrYyyymmdd(time) {
  if (typeof time === 'number'
    && Number.isFinite(time)
    && Number.isInteger(time)
    && time >= 19000101
    && time <= 99991231) {
    const y = Math.floor(time / 10000)
    const mo = String(Math.floor((time % 10000) / 100)).padStart(2, '0')
    const d = String(time % 100).padStart(2, '0')
    return `${y}-${mo}-${d}`
  }
  if (typeof time === 'string' && /^\d{8}$/.test(time)) {
    return `${time.slice(0, 4)}-${time.slice(4, 6)}-${time.slice(6, 8)}`
  }
  return null
}

function dateKeyForDailyBar(bar) {
  if (!bar) return null
  // Prefer `ibkrDayKey` (attached by useDailyPivotSource from the raw IBKR
  // bar before toCandles rewrites `time`). Fall back to `bar.time` for
  // direct-shape callers (tests, future producers). Only fall back to
  // `rawEpochMs` for intraday-shaped bars — daily-anchored bars carry
  // UTC-midnight rawEpochMs which getEtSessionDateKey would misread as
  // the PRIOR ET calendar date.
  const direct = dateKeyFromIbkrYyyymmdd(bar.ibkrDayKey ?? bar.time)
  if (direct) return direct
  if (typeof bar.rawEpochMs === 'number' && Number.isFinite(bar.rawEpochMs)) {
    return getEtSessionDateKey(bar.rawEpochMs)
  }
  return null
}

/**
 * Pick the daily bar whose date key equals the previous ET trading day's
 * key (skipping weekends + holidays). Returns { high, low, close } or null.
 *
 * @param {Array<{time:number|string, rawEpochMs?:number, ibkrDayKey?:string, high:number, low:number, close:number}>} dailyBars
 * @param {string|null} todayEtKey  YYYY-MM-DD ET calendar date for "today"
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {{ high:number, low:number, close:number } | null}
 */
// Shared matching primitive used by both public functions. Walks the
// dailyBars list and returns the first bar whose date key equals the
// previous ET trading day's key (skipping weekends + holidays). Returns
// null when no match exists or when the prior-day walker exhausts its
// safety cap.
function findPriorTradingDayDailyBar(dailyBars, todayEtKey, tradingCalendar) {
  if (!Array.isArray(dailyBars) || dailyBars.length === 0) return null
  if (typeof todayEtKey !== 'string') return null
  const priorKey = getPreviousEtTradingDayKey(todayEtKey, tradingCalendar)
  if (!priorKey) return null
  for (const bar of dailyBars) {
    if (!bar) continue
    const key = dateKeyForDailyBar(bar)
    if (key === priorKey) return bar
  }
  return null
}

export function pickPriorTradingDayDailyBar(dailyBars, todayEtKey, tradingCalendar) {
  const bar = findPriorTradingDayDailyBar(dailyBars, todayEtKey, tradingCalendar)
  return bar ? { high: bar.high, low: bar.low, close: bar.close } : null
}

/**
 * Like {@link pickPriorTradingDayDailyBar} but returns the full matched
 * bar (including `time`, `ibkrDayKey`, and the complete OHLC). Used by
 * the CE-04g-UI-pivots-parity-diagnostic debug readout so the snapshot
 * can surface the raw bar identity the chart selected. Behavior of the
 * normalized helper is unchanged.
 *
 * @param {Array<{time:number|string, rawEpochMs?:number, ibkrDayKey?:string, high:number, low:number, close:number}>} dailyBars
 * @param {string|null} todayEtKey
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {object | null}
 */
export function pickPriorTradingDayDailyBarCandidate(dailyBars, todayEtKey, tradingCalendar) {
  return findPriorTradingDayDailyBar(dailyBars, todayEtKey, tradingCalendar)
}
